import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Instagram, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState(() => {
    const q = new URLSearchParams(window.location.search);
    const print = q.get("print");
    const subject = q.get("subject") || (print ? `Print inquiry · ${print}` : "");
    const message = print
      ? `Hello — please notify me when a signed limited-edition print of "${print}" becomes available.`
      : q.get("message") || "";
    return { name: "", email: "", subject, message };
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. The studio will reply soon.");
      setDone(true);
    } catch (err) {
      toast.error("Couldn't send. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <header className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 md:pb-16">
        <p className="label mb-4">Reach the Studio</p>
        <h1 className="serif text-5xl md:text-7xl tracking-tight font-light leading-[1.02]">
          Contact.
        </h1>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pb-32 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
        <aside className="md:col-span-4 space-y-8">
          <Info icon={<Mail className="h-4 w-4" />} label="Email" value="hello@josephgravesjr.com" />
          <Info icon={<MapPin className="h-4 w-4" />} label="Studio" value="Denver, Colorado" />
          <Info
            icon={<Instagram className="h-4 w-4" />}
            label="Instagram"
            value="@artbyjoseph_graves_jr"
            href="https://www.instagram.com/artbyjoseph_graves_jr"
          />
          <div className="border-t border-border pt-6">
            <p className="label mb-2">Press inquiries</p>
            <p className="text-sm text-[hsl(var(--ink-soft))]">
              For interviews, features, or image requests, please mention "Press"
              in your subject line.
            </p>
          </div>
        </aside>

        <div className="md:col-span-8">
          {done ? (
            <div className="border border-border p-10" data-testid="contact-thanks">
              <p className="label mb-4">Sent</p>
              <h2 className="serif text-4xl tracking-tight">
                Thank you — your message is with the studio.
              </h2>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Your name">
                  <input
                    required
                    value={form.name}
                    onChange={set("name")}
                    className="input"
                    data-testid="contact-name-input"
                  />
                </Field>
                <Field label="Email">
                  <input
                    required
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    className="input"
                    data-testid="contact-email-input"
                  />
                </Field>
              </div>
              <Field label="Subject">
                <input
                  value={form.subject}
                  onChange={set("subject")}
                  className="input"
                  data-testid="contact-subject-input"
                />
              </Field>
              <Field label="Message">
                <textarea
                  required
                  rows={6}
                  value={form.message}
                  onChange={set("message")}
                  className="input resize-none"
                  data-testid="contact-message-input"
                />
              </Field>
              <button
                disabled={busy}
                className="inline-flex items-center gap-3 bg-foreground text-background px-8 py-4 text-xs uppercase tracking-[0.18em] hover:opacity-90 disabled:opacity-50"
                data-testid="contact-submit"
              >
                {busy ? "Sending…" : "Send message"}
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
              <p className="text-xs text-[hsl(var(--ink-soft))] mt-4 max-w-md">
                Your message goes straight to the studio inbox. Your email is
                never shared, sold, or added to any list.
              </p>
            </form>
          )}
        </div>
      </div>

      <style>{`
        .input { width: 100%; background: transparent; border: none; border-bottom: 1px solid hsl(var(--border)); padding: 0.6rem 0; font-size: 1rem; color: hsl(var(--ink)); outline: none; transition: border-color 0.3s ease; }
        .input:focus { border-color: hsl(var(--ink)); }
        textarea.input { border: 1px solid hsl(var(--border)); padding: 1rem; }
        textarea.input:focus { border-color: hsl(var(--ink)); }
      `}</style>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label mb-2 block">{label}</span>
      {children}
    </label>
  );
}

function Info({ icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-3">
      <span className="mt-1 text-[hsl(var(--accent))]">{icon}</span>
      <div>
        <p className="label">{label}</p>
        <p className="serif text-xl mt-1">{value}</p>
      </div>
    </div>
  );
  return href ? (
    <a href={href} target="_blank" rel="noreferrer" className="block link-underline">
      {inner}
    </a>
  ) : (
    inner
  );
}
